import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RCta } from '../RCta';
import { WregPhase } from '../WregPhase';
import { WregRadioGroup, type WregRadioOption } from '../WregRadioGroup';
import { SUBMIT_CONDITION_SCREENING } from '@/consts/events';
import type { WregScreenEvent } from './wregScreenEvents';

type WregScreenerAnswer = 'yes' | 'no';

type WregScreenerOption = WregRadioOption<WregScreenerAnswer>;

type Props = {
  onContinue: () => void;
  onIneligible: () => void;
  onEvent?: WregScreenEvent;
};

const Q1_OPTIONS: WregScreenerOption[] = [
  ['yes', 'wreg.screener.q1.yes'],
  ['no', 'wreg.screener.q1.no'],
];

const WregScreener = ({ onContinue, onIneligible, onEvent }: Props) => {
  const { t } = useTranslation();
  const q1LabelId = useId();
  const [htn, setHtn] = useState<WregScreenerAnswer | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const done = Boolean(htn);
  const submit = () => {
    onEvent?.(SUBMIT_CONDITION_SCREENING);
    return htn === 'yes' ? onContinue() : onIneligible();
  };
  const validateBeforeSubmit = () => {
    if (done) return true;
    setSubmitAttempted(true);
    requestAnimationFrame(() => {
      const firstOption = document
        .getElementById('wreg-screener-q1')
        ?.querySelector<HTMLElement>('[role="radio"]');
      firstOption?.focus();
      firstOption?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
    return false;
  };
  const showError = submitAttempted && !done;
  return (
    <div className="rg-screen">
      <div className="rg-inner rg-inner-head">
        <div className="rg-head-block">
          <WregPhase n={2} />
          <h1 className="rg-h1">{t('wreg.screener.title')}</h1>
          <p className="wreg-screener-sub">{t('wreg.screener.sub')}</p>
        </div>
      </div>
      <div className="rg-list-wrap">
        <div className="rg-list">
          <div className="rg-list-inner rg-opts">
            <div className="wreg-qblock wreg-qcard">
              <div className="wreg-qlabel" id={q1LabelId}>
                {t('wreg.screener.q1.label')}
              </div>
              <WregRadioGroup
                id="wreg-screener-q1"
                value={htn}
                onSelect={setHtn}
                options={Q1_OPTIONS}
                labelledBy={q1LabelId}
                describedBy={showError ? 'wreg-screener-q1-err' : undefined}
                invalid={showError}
              />
              {showError && (
                <span className="rg-error-hint" id="wreg-screener-q1-err" role="alert">
                  Select an option to continue.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="rg-foot">
        <RCta beforeClick={validateBeforeSubmit} dimmed={!done} onClick={submit}>
          {t('wreg.screener.cta')}
        </RCta>
      </div>
    </div>
  );
};

export { WregScreener };
